import React from "react";

import {
  year_to_payment,
  compute_total_income,
  compute_dti_income,
  investment_properties_yearly_amount,
  investment_properties_monthly_amount
} from "../../calculations/income";
import { file_has_dscr_income } from "../../calculations/dscr";

import BorrowerIncomeInformation from "./borrower_income_information";

import CurrencyField from "../fields/currency_field";

import { useDocument } from "../loan_tools/loan_tool";
import Section from "../ui/section";
import { wrap_investment_properties } from "../../calculations/investment_reo";

export default function BorrowerIncomeTable({
  showEditIncomeSource,
  showEditAdditionalIncome,
  onChangeBorrowerIncome
}) {
  const [{ investment_properties, applicant, property }] = useDocument();
  const { borrowers } = applicant;
  const wrapped_investment_properties = wrap_investment_properties(
      property, investment_properties);
  const investReoMonthly = investment_properties_monthly_amount(
    wrapped_investment_properties
  );
  const investReoYearly = investment_properties_yearly_amount(
    wrapped_investment_properties
  );

  // When the file uses the DSCR calculator, split the Summary so the LO can see
  // the two qualifying methods separately (they key different loan types):
  //   • DSCR Rent Received   -> qualifies the DSCR loan (the DSCR ratio)
  //   • Full Doc Income      -> qualifies a Fannie/Government/full-doc loan
  //     (W-2 / Self-Employed + Invest REO Schedule E net); excludes DSCR rents
  // Combined = DSCR + Full Doc, so the numbers reconcile.
  const hasDscr = file_has_dscr_income({ applicant });
  const combinedYearly = compute_total_income(
    borrowers,
    wrapped_investment_properties
  );
  const fullDocYearly = compute_dti_income(
    borrowers,
    wrapped_investment_properties
  );
  const dscrYearly = combinedYearly.sub(fullDocYearly);

  return (
    <div>
      {hasDscr && (
        <div className="income-dscr-reminder no-print">
          <b>Running a full-doc comparison?</b> A DSCR loan qualifies on the
          property&apos;s rent coverage, so <b>DSCR rents do not count toward
          DTI</b>. If you&apos;re also running a Fannie Mae / Government /
          full-doc loan on this 1&ndash;4 unit property, add the borrower&apos;s
          W-2 or Self-Employed income with the <b>Add Income Source</b> button
          below &mdash; that documented income (plus the Invest REO Schedule E
          net) is what the DTI uses.
        </div>
      )}
      <div>
        {borrowers.map((borrower, index) => (
          <BorrowerIncomeInformation
            key={index}
            borrower={borrower}
            borrowerIndex={index}
            showEditAdditionalIncome={showEditAdditionalIncome}
            showEditIncomeSource={showEditIncomeSource}
            onChangeBorrowerIncome={onChangeBorrowerIncome}
          />
        ))}
      </div>
      {borrowers.length !== 0 && (
        <Section
          name="Summary"
          className={hasDscr ? "income-summary" : "income-summary half-width"}
        >
          {hasDscr ? (
            <>
              <div className="income-summary-group">
                <CurrencyField
                  readOnly
                  name="DSCR Rent Received — Monthly (qualifies the DSCR loan)"
                  value={year_to_payment(dscrYearly, 12)}
                />
                <CurrencyField
                  readOnly
                  name="DSCR Rent Received — Yearly"
                  value={dscrYearly}
                />
              </div>
              <div className="income-summary-group">
                <CurrencyField
                  readOnly
                  name="Full Doc Income — Monthly (W-2 / Self-Employed + Invest REO Net)"
                  value={year_to_payment(fullDocYearly, 12)}
                />
                <CurrencyField
                  readOnly
                  name="Full Doc Income — Yearly (qualifies a Fannie / Gov / full-doc loan)"
                  value={fullDocYearly}
                />
              </div>
              <div className="income-summary-group income-summary-group-total">
                <CurrencyField
                  readOnly
                  name="Combined Monthly Total of ALL Income"
                  value={year_to_payment(combinedYearly, 12)}
                />
                <CurrencyField
                  readOnly
                  name="Combined Yearly Total of ALL Income"
                  value={combinedYearly}
                />
              </div>
            </>
          ) : (
            <>
              {investReoMonthly.gt(0) && (
                <CurrencyField
                  readOnly
                  name="Positive Income from Investment REOs Monthly"
                  value={investReoMonthly}
                />
              )}
              {investReoYearly.gt(0) && (
                <CurrencyField
                  readOnly
                  name="Positive Income from Investment REOs Yearly"
                  value={investReoYearly}
                />
              )}
              <CurrencyField
                readOnly
                name="Combined Monthly Total of ALL Income"
                value={year_to_payment(combinedYearly, 12)}
              />
              <CurrencyField
                readOnly
                name="Combined Yearly Total of ALL Income"
                value={combinedYearly}
              />
            </>
          )}
        </Section>
      )}
    </div>
  );
}
